/**
 * The options an owner picker offers, including whoever already holds it.
 *
 * THE BUG THIS EXISTS FOR. The picker took its value from the ticket and its
 * options from the group's agent list. When the assignee was not in that list
 * the control matched nothing and rendered blank, so a ticket that WAS
 * assigned read as unassigned — while the row beside it showed the
 * assignee's initials. 158 of 159 imported tickets looked like that, because
 * their FreshService assignees had never been granted a role in the group.
 *
 * Granting those roles is the real fix and is done at import. This is the
 * other half: an assignee can always fall off the list later — someone
 * leaves the group, or their grant is revoked — and when they do, their open
 * tickets must not quietly start reading as unassigned. Whoever holds the
 * ticket is always offered, and is labelled so the reader knows why they are
 * not a normal choice.
 */

export interface OwnerOption {
  value: string;
  label: string;
}

/** The shape every agent list has, from `GET /config`. */
export interface AgentRef {
  oid: string;
  name: string | null;
  email: string;
}

/**
 * Label agents so no two read the same.
 *
 * Several people here hold more than one account — the import made a row per
 * address, so the same human appears as `chewitt@esassoc.com` and
 * `clancy.hewitt@sitkatech.com`. Labelled by name alone they become two
 * identical lines, and choosing between them is a coin flip that decides
 * whether the assignee ever sees the ticket.
 *
 * Only the colliding names carry an address. Showing one against every agent
 * would make the list harder to read for everybody in order to fix it for
 * six.
 */
export function labelAgents(agents: readonly AgentRef[]): { oid: string; label: string }[] {
  const seen = new Map<string, number>();
  for (const a of agents) {
    const key = (a.name ?? '').trim().toLowerCase();
    if (key) seen.set(key, (seen.get(key) ?? 0) + 1);
  }

  return agents.map((a) => {
    const key = (a.name ?? '').trim().toLowerCase();
    // No name at all means the label is already the address, which is unique.
    const collides = key !== '' && (seen.get(key) ?? 0) > 1;
    return {
      oid: a.oid,
      label: collides ? `${a.name} (${a.email})` : (a.name ?? a.email),
    };
  });
}

export function ownerOptions(
  current: { oid: string | null; name?: string | null; email?: string | null },
  agents: readonly { oid: string; name: string | null; email: string }[] | undefined,
): OwnerOption[] {
  const list = agents ?? [];
  const options: OwnerOption[] = [
    { value: '', label: 'Unassigned' },
    ...labelAgents(list).map((a) => ({ value: a.oid, label: a.label })),
  ];

  if (!current.oid) return options;
  if (list.some((a) => a.oid === current.oid)) return options;

  // Named rather than shown as a bare id: "imported-eb6df8a45ec449a7" tells
  // the reader nothing, and an option with no label looks like a glitch.
  const who = current.name ?? current.email ?? 'Someone no longer listed';
  return [...options, { value: current.oid, label: `${who} — not in this group` }];
}
